import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, FormDialog, Grid, InputField } from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { selectors as authSelectors } from 'store/auth';
import { actions as kioskActions, selectors as kioskSelectors } from 'store/kiosks';
import { useStyles } from '../styled';
import { BaseOverlayProps } from '../types';

const RestockOverlay = (props: BaseOverlayProps) => {
  const { isOpen, onClose, kiosk } = props;

  const { updateKiosk, logEvent, resetAction } = useActions({
    updateKiosk: kioskActions.updateKiosk,
    logEvent: kioskActions.logEvent,
    resetAction: kioskActions.resetAction
  });

  const admin = useSelector(authSelectors.selectAdmin);
  const { success } = useSelector(kioskSelectors.selectAction);

  const [stockValues, setStockValues] = useState<{ [key: string]: number }>({});

  const classes = useStyles({ showOverlay: isOpen })();

  useEffect(() => {
    if (isOpen && success) {
      onClose();
      resetAction();
    }
    setStockValues({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, success]);

  const validateAndSetStockValues = (value: string, kioskHardwareId: string) => {
    if (value && !isNaN(Number(value)) && Number(value) >= 0) {
      setStockValues((prevState) => ({
        ...prevState,
        [kioskHardwareId]: Math.abs(Number(value))
      }));
    } else if (!value) {
      setStockValues((prevState) => {
        const { [kioskHardwareId]: _, ...rest } = prevState;
        return rest;
      });
    }
  };

  // on click data entry overlay
  const onClickSave = () => {
    // Include all hardware Ids, otherwise they would be deleted.
    updateKiosk({
      id: kiosk.id,
      hardware: kiosk.hardware.map((h) => {
        if (stockValues.hasOwnProperty(h.id)) {
          return { id: h.id, values: { currentStock: stockValues[h.id] } };
        }
        return { id: h.id };
      })
    });

    // kioskId needs to be replaced with exact data.
    logEvent({ kioskId: kiosk.id, adminId: admin ? admin.id : '' });
  };

  return (
    <FormDialog
      cancelButtonAction={onClose}
      cancelButtonText="Cancel"
      confirmButtonAction={onClickSave}
      confirmButtonDisabled={Object.keys(stockValues).length === 0}
      confirmButtonText="Save"
      open={isOpen}
      title="Load Ticket Stock"
    >
      <Grid>
        {kiosk?.hardware
          ?.filter((h) => h.type === 'printer')
          ?.map((printer, idx) => (
            <Box className={classes.restockOverlayFieldBox} key={`restock-box-${idx}`}>
              <InputField
                label={`${printer.configuration.displayName || ''} Printer`}
                size="small"
                align="center"
                sx={{ width: { xs: '262px', sm: '175px' } }}
                placeholder={(printer.values.currentStock || 0).toString()}
                value={stockValues.hasOwnProperty(printer.id) ? stockValues[printer.id] : ''}
                onChange={(e) => validateAndSetStockValues(e.target.value, printer.id)}
                data-testid={`restock-input-${idx}`}
              />
            </Box>
          ))}
      </Grid>
    </FormDialog>
  );
};

export default RestockOverlay;
